/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect, useState } from "react";
import { AccountContext, ContextUpdater } from "../../../context/ContextProvider";
import { Alert, Label } from "reactstrap";
import { getCurrentTimestamp, secondsToTimeString, unixEpochToString } from "../../../util/DateTime";
import { requestClientActivityList } from "../../../context/RequestManager";
import ScrollableDiv from "../../../elements/ScrollableDiv";
import LoadingSpinner from "../../../elements/LoadingSpinner";
import RefreshButton from "../../../elements/RefreshButton";
import ItemLabel from "../../../util/Icons";
import debug from "../../../util/constants/Debug";
import { isIPad } from "../../../util/UserAgent";

const ActivityItem = (props) => {
  // parse out prop attributes
  const { currentTimestamp = getCurrentTimestamp(), selected, type, guestType, marketing } = props;

  const getActivityText = (props) => {
    // parse out props
    const { routerMacAddress, deviceMacAddress, guestName, emailName, timestamp } = props;

    // prepare time
    const time = secondsToTimeString(currentTimestamp - timestamp, " ago");
    
    // return element
    switch (props?.type) {
      case "guest_connected_wifi":
        return (
          <span>
            Guest <span className="fw-bold">{guestName}</span> connected to WiFi {time}
          </span>
        );
      case "guest_disconnected_wifi":
        return (
          <span>
            Guest <span className="fw-bold">{guestName}</span> disconnected to WiFi {time}
          </span>
        );
      case "guest_opened_email":
        return (
          <span>
            Guest <span className="fw-bold">{guestName}</span> opened email <span className="fw-bold">{emailName}</span> {time}
          </span>
        );
      case "router_channel_auto_optimized":
        return (
          <span>
            WiFi signal auto-optimized for router <span className="fw-bold">{routerMacAddress}</span> {time}
          </span>
        );
      case "device_connected_wifi":
        return (
          <span>
            Device <span className="fw-bold">{deviceMacAddress}</span> connected to WiFi {time}
          </span>
        );
      case "device_disconnected_wifi":
        return (
          <span>
            Device <span className="fw-bold">{deviceMacAddress}</span> disconnected to WiFi {time}
          </span>
        );
      default: return null;
    }
  };

  const getActivityIcon = (type, guestType) => {
    switch (type) {
      case "guest_connected_wifi":
      case "guest_disconnected_wifi":
        switch (guestType) {
          case "email":
            return "envelope"
          case "apple":
            return "apple"
          case "google":
            return "google"
          case "facebook":
            return "facebook"
          case "twitter":
            return "twitter"
          case "linked_in":
            return "linkedin"
          case "sms":
            return "chat-dots"
          default:
            return "wifi";
        }
      case "guest_opened_email":
        return "envelope-open"
      case "router_channel_auto_optimized":
        return "router"
      case "device_connected_wifi":
      case "device_disconnected_wifi":
        return "phone"
      default: return null;
    }
  };

  return (
    <Alert
      className={`mx-2 d-flex align-items-center no-select clickable ${selected ? "selected" : "not-selected"}`}
      color={marketing ? "marketing" : "primary"}
      style={{ padding: "10px" }}
    >
      <ItemLabel className="no-spacing" icon={getActivityIcon(type, guestType)}>
        {getActivityText(props)}
      </ItemLabel>
    </Alert>
  );
};

const LiveActivityFeed = (props) => {
  // context
  const contextUpdater = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);

  const [state, setState] = useState({
    activityList: null,
    lastRefresh: null,
    tickList: null,
    lastId: null,
  });

  const locationId = props.locationId;

  // update handler
  const updateTicker = (showLoading = false) => {
    requestClientActivityList({
      context: contextUpdater,
      token: accountContext.token,
      clientId: accountContext.clientId,
      limit: 100,
      showLoading: showLoading,
      locationId: locationId ? locationId : null,
      completion: (results) => {
        // get activity list
        if (results.error) {
          debug.warn("Could not get activities: " + results.error);
          return;
        }
      }
    });
  };

  useEffect(() => {
    const activityList = accountContext?.activityList;
    if (activityList == null) {
      debug.warn("Could not get activities");
      return;
    }

    setState(prevState => {
      // set state
      const newState = {
        activityList: activityList,
        lastId: activityList[0]?.id,
      };

      if (prevState.lastId == null) {
        // initial list
        newState.tickList = [];
      } else {
        // prepare new tick items
        const newTickItems = [];
        activityList.every(activity => {
          // end iterating if checking old activity
          if (activity.id <= prevState.lastId) {
            return false;
          }
          
          // add new activity to tick list
          newTickItems.push({ id: activity.id, ticksLeft: 1 });
          return true;
        });

        // process existing tick items
        const oldTickItems = prevState.tickList
          .map(item => ({ ...item, ticksLeft: item.ticksLeft - 1}))
          .filter(item => item.ticksLeft > 0);

        newState.tickList = [...oldTickItems, ...newTickItems];
      }

      // get current time
      newState.lastRefresh = getCurrentTimestamp();

      return newState;
    });        
  }, [accountContext?.activityList])

  useEffect(() => {
    setState(prevState => ({...prevState, lastId: null }));
    const interval = setInterval(updateTicker, 5 * 60 * 1000 /* 5 minutes */);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  return (
    <div className="fill-container d-flex flex-column justify-content-center align-items-center"  style={{ ...(isIPad() ? {maxHeight: "100%"} : {})}}>
      <div className="w-100 d-flex justify-content-between align-items-start" style={{...(isIPad() ? { minHeight: "44px"} : {})}}>
        <div style={{ width: "30px" }} />
        <Label className="text-center flex-static" style={{ paddingTop: "1px", fontSize: "22px" }}>Live Activity Feed</Label>
        <RefreshButton onClick={() => updateTicker(true)}/>
      </div>
      {state.lastRefresh && (
        <Label className="w-100 text-center no-spacing" size="sm">
          Last updated at
          <span className="fw-bold">
            {' '}{unixEpochToString(state.lastRefresh)}
          </span>
        </Label>
      )}
      <div className="pt-2 w-100 flex-dynamic d-flex">
        {state.activityList == null && (
          <LoadingSpinner />
        )}
        {state.activityList?.length === 0 && (
          <Label className="pt-3 w-100 text-center text-muted">
            No activity available...
          </Label>
        )}
        {state.activityList?.length > 0 && (
          <ScrollableDiv hideScroll>
            {state.activityList.map((activity) => (
              <ActivityItem
                key={activity.id}
                selected={state.tickList?.some(item => item.id === activity.id) ?? false}
                {...activity}
              />
            ))}
          </ScrollableDiv>
        )}
      </div>
    </div>
  );
};

export default LiveActivityFeed;
